import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Pencil, Trash2, Calendar as CalendarIcon, Clock, User, Phone, Music2, Disc3, Sparkles, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, getToken, setToken, clearToken } from "@/lib/api";
import { useI18n } from "@/i18n";
import LangSwitcher from "@/components/LangSwitcher";

const SERVICE_OPTIONS = [
  { value: "mix", label: "Session Mix", icon: Disc3, defaultPrice: 30 },
  { value: "prod", label: "Prod personnalisée", icon: Sparkles, defaultPrice: 50 },
  { value: "mastering", label: "Mastering", icon: Music2, defaultPrice: 50 },
  { value: "autre", label: "Autre", icon: CalendarIcon, defaultPrice: 0 },
];

const serviceMeta = (v) => SERVICE_OPTIONS.find((s) => s.value === v) || SERVICE_OPTIONS[3];

export default function Admin() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [authed, setAuthed] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      client_name: "",
      phone: "",
      service: "mix",
      date: format(new Date(), "yyyy-MM-dd"),
      start_time: "14:00",
      duration_hours: 1,
      price_eur: 30,
      notes: "",
    };
  }

  useEffect(() => {
    if (authed) {
      api.verify().then(() => loadBookings()).catch(() => {
        clearToken();
        setAuthed(false);
      });
    }
  }, [authed]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await api.listBookings();
      setBookings(res.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await api.login(password);
      setToken(res.data.token);
      setAuthed(true);
      setPassword("");
      toast.success(t("admin.login.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.login.wrong"));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    toast.success("Déconnecté");
  };

  const openCreate = (date) => {
    setEditing(null);
    setForm({ ...emptyForm(), date: format(date || selectedDate, "yyyy-MM-dd") });
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      client_name: b.client_name,
      phone: b.phone || "",
      service: b.service,
      date: b.date,
      start_time: b.start_time,
      duration_hours: b.duration_hours,
      price_eur: b.price_eur ?? 0,
      notes: b.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, duration_hours: parseFloat(form.duration_hours) || 1, price_eur: parseFloat(form.price_eur) || 0 };
      if (editing) {
        await api.updateBooking(editing.id, payload);
        toast.success("Réservation mise à jour");
      } else {
        await api.createBooking(payload);
        toast.success("Réservation créée");
      }
      setDialogOpen(false);
      loadBookings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette réservation ?")) return;
    try {
      await api.deleteBooking(id);
      toast.success("Réservation supprimée");
      loadBookings();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const bookedDates = useMemo(
    () => bookings.map((b) => parseISO(b.date)).filter(Boolean),
    [bookings]
  );

  const dayBookings = useMemo(() => {
    return bookings
      .filter((b) => isSameDay(parseISO(b.date), selectedDate))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [bookings, selectedDate]);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookings
      .filter((b) => parseISO(b.date) >= today)
      .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time))
      .slice(0, 6);
  }, [bookings]);

  // ---------- LOGIN VIEW ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <button
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-2"
              data-testid="back-to-site"
            >
              {t("admin.login.back")}
            </button>
            <LangSwitcher />
          </div>
          <div className="border border-white/10 rounded-2xl p-10 bg-[#0F0F11]">
            <div className="flex items-center gap-2 text-cyan-200/90 mb-2">
              <Lock className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.25em] font-mono-tech">{t("admin.login.eyebrow")}</span>
            </div>
            <h1 className="font-heading text-3xl tracking-tight font-light mb-2" data-testid="admin-login-title">{t("admin.login.title")}</h1>
            <p className="text-sm text-zinc-500 mb-8">{t("admin.login.subtitle")}</p>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="pwd" className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">{t("admin.login.password")}</Label>
                <Input
                  id="pwd"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-black border-white/15 text-white h-11"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full h-11 bg-white text-black hover:bg-cyan-200 rounded-full font-medium"
                data-testid="admin-login-submit"
              >
                {loginLoading ? t("admin.login.loading") : t("admin.login.submit")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ---------- ADMIN DASHBOARD ----------
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-cyan-200 shadow-[0_0_12px_#A5F3FC]" />
            <span className="font-heading text-lg tracking-tight font-medium">Ice Label</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech ml-3 hidden sm:inline">/ Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => openCreate(selectedDate)}
              className="bg-white text-black hover:bg-cyan-200 rounded-full h-9 px-4 text-xs font-medium"
              data-testid="add-booking-button"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Nouvelle réservation
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-full h-9 px-3 text-xs"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-3">Tableau de bord</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light" data-testid="admin-page-title">
            Calendrier des réservations
          </h1>
          <p className="text-zinc-500 text-sm mt-3">{bookings.length} réservation{bookings.length > 1 ? "s" : ""} enregistrée{bookings.length > 1 ? "s" : ""}.</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-5">
            <div className="border border-white/10 rounded-2xl bg-[#0F0F11] p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech">Calendrier</span>
                <CalendarIcon className="h-4 w-4 text-zinc-500" />
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{
                  booked: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-cyan-200",
                }}
                locale={fr}
                className="rounded-md"
                data-testid="admin-calendar"
              />
            </div>
          </div>

          {/* Day bookings */}
          <div className="lg:col-span-7">
            <div className="border border-white/10 rounded-2xl bg-[#0F0F11] p-6 lg:p-8 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech mb-1">Sélectionné</p>
                  <h2 className="font-heading text-2xl tracking-tight font-medium capitalize" data-testid="selected-day-label">
                    {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                  </h2>
                </div>
                <Button
                  onClick={() => openCreate(selectedDate)}
                  variant="outline"
                  className="border-white/15 hover:bg-white hover:text-black rounded-full h-9 text-xs"
                  data-testid="add-on-day-button"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter
                </Button>
              </div>

              {loading ? (
                <p className="text-sm text-zinc-500">Chargement...</p>
              ) : dayBookings.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
                  <p className="text-sm text-zinc-500">Aucune réservation ce jour-là</p>
                </div>
              ) : (
                <ul className="space-y-3" data-testid="day-bookings-list">
                  {dayBookings.map((b) => {
                    const meta = serviceMeta(b.service);
                    const Icon = meta.icon;
                    return (
                      <li
                        key={b.id}
                        className="border border-white/10 hover:border-cyan-200/30 rounded-xl p-4 sm:p-5 bg-black/30 transition-colors"
                        data-testid={`booking-item-${b.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="hidden sm:flex h-10 w-10 rounded-lg border border-white/10 items-center justify-center bg-cyan-200/5">
                            <Icon className="h-4 w-4 text-cyan-200" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                              <span className="font-heading text-base font-medium">{b.client_name}</span>
                              <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-200/80 font-mono-tech">{meta.label}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                              <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />{b.start_time} · {b.duration_hours}h</span>
                              {b.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3" />{b.phone}</span>}
                              {!!b.price_eur && <span className="text-cyan-200/90">{b.price_eur}€</span>}
                            </div>
                            {b.notes && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{b.notes}</p>}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" data-testid={`booking-menu-${b.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#0F0F11] border-white/10">
                              <DropdownMenuItem onClick={() => openEdit(b)} className="text-zinc-300 focus:bg-white/5 focus:text-white">
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(b.id)} className="text-red-400 focus:bg-red-500/10 focus:text-red-300">
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Upcoming */}
            <div className="border border-white/10 rounded-2xl bg-[#0F0F11] p-6 lg:p-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech mb-5">Prochaines sessions</p>
              {upcoming.length === 0 ? (
                <p className="text-sm text-zinc-500">Aucune session à venir</p>
              ) : (
                <ul className="divide-y divide-white/5" data-testid="upcoming-list">
                  {upcoming.map((b) => {
                    const meta = serviceMeta(b.service);
                    return (
                      <li
                        key={b.id}
                        className="py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-white/[0.02] -mx-2 px-2 rounded-lg transition-colors"
                        onClick={() => setSelectedDate(parseISO(b.date))}
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="text-center min-w-[44px]">
                            <p className="font-heading text-lg leading-none">{format(parseISO(b.date), "dd")}</p>
                            <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1 font-mono-tech">{format(parseISO(b.date), "MMM", { locale: fr })}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{b.client_name}</p>
                            <p className="text-xs text-zinc-500">{meta.label} · {b.start_time}</p>
                          </div>
                        </div>
                        {!!b.price_eur && <span className="text-xs text-cyan-200/80 font-mono-tech">{b.price_eur}€</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F11] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight font-light">
              {editing ? "Modifier la réservation" : "Nouvelle réservation"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Renseignez les détails de la session.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Client</Label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  required
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-client-name"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Téléphone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-phone"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Service</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => {
                    const meta = serviceMeta(v);
                    setForm({ ...form, service: v, price_eur: meta.defaultPrice || form.price_eur });
                  }}
                >
                  <SelectTrigger className="mt-1.5 bg-black border-white/15 text-white" data-testid="form-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F11] border-white/10 text-white">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="focus:bg-white/5 focus:text-white">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-date"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Heure</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  required
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-time"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Durée (h)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={form.duration_hours}
                  onChange={(e) => setForm({ ...form, duration_hours: e.target.value })}
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-duration"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Prix (€)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.price_eur}
                  onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                  className="mt-1.5 bg-black border-white/15 text-white"
                  data-testid="form-price"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-tech">Notes</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1.5 bg-black border-white/15 text-white resize-none"
                  data-testid="form-notes"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400 hover:text-white">
                Annuler
              </Button>
              <Button type="submit" className="bg-white text-black hover:bg-cyan-200 rounded-full px-6" data-testid="form-submit">
                {editing ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
